import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppState } from 'src/app/store/app.reducer';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { MtmaAction } from 'src/app/model/mtma-action';
import { MtmaDtmf } from 'src/app/model/mtma-dtmf';
import { CustomValidators } from 'src/app/validators/custom.validators';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { StandardNewValidator, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';

@Component({
	selector: 'app-mtma-manage-dtmf-tone-dialog',
	templateUrl: './mtma-manage-dtmf-tone-dialog.component.html',
	providers: [{
		provide: STEPPER_GLOBAL_OPTIONS, useValue: { showError: true }
	}]
})

export class MtmaDtmfToneDialogComponent implements OnInit, OnDestroy {
	dtmfForm: FormGroup;

	mtmaDtmfModifySub: Subscription;
	dtmfTone: MtmaDtmf;

	adding = true;

	mtmaActionsSub: Subscription;

	mtmaActions: MtmaAction[] = [];
	dtmfs: MtmaDtmf[];
	dtmfsSub: Subscription;

	screenWidth: number;

	constructor(
		private fb: FormBuilder,
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
	) {
		if (config.data !== undefined && config.data.dtmfTone !== undefined) {
			this.dtmfTone = config.data.dtmfTone;
			this.adding = false;
		} else {
			this.dtmfTone = new MtmaDtmf();
		}
	}

	ngOnInit(): void {
		this.store.dispatch(DeviceActions.getMtmaDtmfs({}));
		this.dtmfForm = new FormGroup({
			'Description': new FormControl(this.dtmfTone.Description, [Validators.minLength(1), Validators.maxLength(48)]),
			'Code': new FormControl(this.dtmfTone.Code, [Validators.required, Validators.minLength(2),
			Validators.maxLength(3), Validators.pattern('[0-9#]+')]),
			'Action': new FormControl(this.dtmfTone.Action, Validators.required),
		});
		this.dtmfForm.setValidators(CustomValidators.checkEndHashtag('Code'));

		this.mtmaDtmfModifySub = this.store.select('device', 'mtmaDtmfModify')
			.pipe(filter(mtmaDtmfModifySub => !!mtmaDtmfModifySub))
			.subscribe(() => {
				this.resetClose();
			});

		this.mtmaActionsSub = this.store.select('device', 'mtmaActions')
			.subscribe(mtmaActions => {
				this.mtmaActions = mtmaActions.filter(a => a.DtmfTones && a.Id !== 255);
			}
			);

		this.dtmfsSub = this.store.select('device', 'mtmaDtmfs').subscribe(dtmfs => {
			this.dtmfs = dtmfs;
			this.filterActions();
		}
		);

		StandardNewValidator.setFirstCompile(true);

		this.onResize();
	}

	filterActions(): void {
		if (this.mtmaActions !== undefined && this.mtmaActions.length > 0 && this.dtmfs !== undefined && this.dtmfs.length > 0) {
			const list = [];
			for (const action of this.mtmaActions) {
				if (!this.dtmfs.find(el => el.Action === action.Id && this.dtmfTone.Action !== action.Id)) {
					list.push(action);
				}
			}
			this.mtmaActions = list;
		}
	}

	ngOnDestroy(): void {
		this.mtmaDtmfModifySub?.unsubscribe();
		this.mtmaActionsSub?.unsubscribe();
		this.dtmfsSub?.unsubscribe();
	}

	resetClose(): void {
		this.store.dispatch(DeviceActions.resetDtmfForm());
		this.closeDialog();
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.dtmfForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.dtmfForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	save() {
		this.dtmfTone = {
			...this.dtmfTone,
			Description: this.f.Description.value,
			Code: this.f.Code.value,
			Action: this.f.Action.value
		};
		if (this.dtmfTone.ObjId) {
			this.store.dispatch(DeviceActions.editMtmaDtmf({ dtmf: this.dtmfTone }));
		} else {
			this.store.dispatch(DeviceActions.addMtmaDtmf({ dtmf: this.dtmfTone }));
		}
	}

	get f(): any {
		return this.dtmfForm.controls;
	}

	closeDialog(): void {
		this.ref.close();
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	col12Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-30rem');
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	newShowErrors(type: MtmaDtmfToneDialogComponent["f"], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: MtmaDtmfToneDialogComponent["f"]): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}
}
